import { Link, type Href } from 'expo-router';
import Head from 'expo-router/head';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import MapPin from 'lucide-react-native/icons/map-pin';
import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { TourCard } from '@/components/riviera/tour-card';
import { WineTourTable } from '@/components/riviera/wine-tour-table';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { useTours } from '@todo/core/hooks/use-tours';
import { cities } from '@/lib/events-data';
import { cityPageCopy } from '@/lib/i18n/city-content';
import { currencySymbol } from '@todo/core/i18n/format';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { wineFaq, winePageCopy, wineRegionSections } from '@/lib/i18n/wine-content';
import {
  LIST_BUCKET,
  WINE_TOURS_INDEX_QUERY,
  WINE_TOURS_INDEX_SEED_KEY,
  WINE_TOUR_CITIES_SEED_KEY,
  wineToursCityQuery,
  wineToursCitySeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  seoTitle,
  toursCrumb,
  truncate,
  wineToursCrumb,
} from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';
import type { Tour } from '@todo/core/tours';
import { MIN_WINE_TOURS_PER_PAGE, splitWineTours, wineTourCityIds } from '@/lib/wine-tours';

function minPriceLabel(tours: Tour[]): string | null {
  let best: Tour | null = null;
  for (const tour of tours) {
    if (tour.from_price == null) continue;
    if (!best || tour.from_price < (best.from_price ?? Infinity)) best = tour;
  }
  if (!best) return null;
  const symbol = currencySymbol(best.currency);
  return `${symbol}${Math.round(best.from_price!)}`;
}

/**
 * The /[lang]/wine-tours landing pages — the regional hub (no `cityId`) and one
 * page per departure city. Built for the "wine tour cannes" / "wine tasting
 * cannes" / "provence wine tour from cannes" searches Search Console showed
 * the site had no page for: a query-matching <title>/<h1>, hand-written
 * per-city intro, the city's wine tours (tours_by_theme, seeded at export so
 * the cards are real links in the static HTML) and a FAQ. The tours show
 * twice: a comparison table (price / duration / format / reviews) under the
 * list heading, then the cards split into group and private tours — the
 * private block's own <h2> targets "private wine tour {city}" searches.
 */
export function WineToursScreen({ cityId }: { cityId?: string }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const city = cityId ? cities.find((c) => c.id === cityId) : undefined;
  const copy = cityId ? (city ? winePageCopy(locale, city.id) : undefined) : winePageCopy(locale);
  const faq = wineFaq(locale);

  // Always called (rules of hooks). An unknown city id only arrives via a bad
  // client-side navigation — generateWineCityStaticParams never emits one —
  // and just returns no rows.
  const { tours, loading, error } = useTours(
    cityId ? wineToursCityQuery(cityId) : WINE_TOURS_INDEX_QUERY,
    cityId ? (city ? wineToursCitySeedKey(city.id) : undefined) : WINE_TOURS_INDEX_SEED_KEY,
  );

  // Which cities have their own page. On web this is the exact list the export
  // generated pages for (baked into the HTML); on native, where every route
  // resolves, it's derived from the hub's rows (empty on a city page, which
  // then only links back to the hub).
  const [bakedCityIds] = useState(() =>
    getPrefetched<string[]>(LIST_BUCKET, WINE_TOUR_CITIES_SEED_KEY),
  );
  const pageCityIds = bakedCityIds ?? (cityId ? [] : wineTourCityIds(tours));

  if (!copy) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground, paddingHorizontal: Spacing.three }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const path = city ? `wine-tours/${city.id}` : 'wine-tours';
  const canonical = absoluteUrl(locale, path);
  const title = seoTitle(copy.seoTitle);
  const metaDescription = truncate(copy.metaDescription);
  const ogImage = richResultImage(tours[0]?.image_url);
  // Too few tours to be worth indexing — see MIN_WINE_TOURS_PER_PAGE. Only
  // once loaded, so a page still fetching client-side isn't flagged.
  const thin = !loading && tours.length < MIN_WINE_TOURS_PER_PAGE;
  const price = minPriceLabel(tours);
  const split = splitWineTours(tours);
  const cityGuide = city ? cityPageCopy(city.id, locale) : undefined;
  // Hub only: the regional guide text and "Wine Tours from Nice"-style anchors
  // to each city page (stronger anchor text than the chips' bare city names).
  const sections = city ? [] : wineRegionSections(locale);
  const cityLinks = city
    ? []
    : pageCityIds.flatMap((id) => {
        const cityCopy = winePageCopy(locale, id);
        return cityCopy ? [{ id, label: cityCopy.title }] : [];
      });

  const trail = city
    ? [toursCrumb(locale), wineToursCrumb(locale), { name: city.name }]
    : [toursCrumb(locale), { name: wineToursCrumb(locale).name }];

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <link rel="canonical" href={canonical} />
        {hreflangLinks(path).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, trail))}
        </script>
        {tours.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                tours.map((tour) => ({
                  name: localizedTourTitle(tour, locale),
                  url: absoluteUrl(locale, `tours/${tour.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(city ? wineToursCitySeedKey(city.id) : WINE_TOURS_INDEX_SEED_KEY)}
        {listSeedScript(WINE_TOUR_CITIES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {copy.title}
            </Text>
            {tours.length > 0 && price ? (
              <Text style={[styles.stats, { color: theme.gold }]}>
                {t('wineTours.stats', { count: tours.length, price })}
              </Text>
            ) : null}
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[styles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            {pageCityIds.length > 0 ? (
              <View style={styles.citiesBox}>
                <Text style={[styles.citiesHeading, { color: theme.mutedForeground }]}>
                  {t('wineTours.citiesHeading')}
                </Text>
                <View style={styles.chipsRow}>
                  <CityChip
                    label={t('wineTours.allCities')}
                    active={!city}
                    href={{ pathname: '/[lang]/wine-tours', params: { lang: locale } }}
                  />
                  {pageCityIds.map((id) => {
                    const name = cities.find((c) => c.id === id)?.name ?? id;
                    return (
                      <CityChip
                        key={id}
                        label={name}
                        active={city?.id === id}
                        href={{
                          pathname: '/[lang]/wine-tours/[city]',
                          params: { lang: locale, city: id },
                        }}
                      />
                    );
                  })}
                </View>
              </View>
            ) : null}

            <Text
              role="heading"
              aria-level={2}
              style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {copy.listHeading}
            </Text>
            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('wineTours.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : tours.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('wineTours.empty')}
              </Text>
            ) : (
              <>
                <Text style={[styles.note, { color: theme.mutedForeground }]}>
                  {t('wineTours.compare.note')}
                </Text>
                <WineTourTable tours={tours} showCity={!city} />

                {/* Group first: it's the cheaper format most visitors book.
                    Either half is skipped when empty, so its heading never
                    sits over nothing. */}
                {split.group.length > 0 ? (
                  <>
                    <Text
                      role="heading"
                      aria-level={2}
                      style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
                      {t('wineTours.groupHeading')}
                    </Text>
                    <View style={styles.grid}>
                      {/* First row is above the fold on desktop — eager, as on /tours. */}
                      {split.group.map((tour, index) => (
                        <TourCard key={tour.id} tour={tour} eager={index < 4} />
                      ))}
                    </View>
                  </>
                ) : null}

                {split.private.length > 0 ? (
                  <>
                    <Text
                      role="heading"
                      aria-level={2}
                      style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
                      {copy.privateHeading}
                    </Text>
                    <Text style={[styles.intro, { color: theme.text }]}>
                      {t('wineTours.privateIntro')}
                    </Text>
                    <View style={styles.grid}>
                      {split.private.map((tour, index) => (
                        <TourCard
                          key={tour.id}
                          tour={tour}
                          eager={split.group.length === 0 && index < 4}
                        />
                      ))}
                    </View>
                  </>
                ) : null}
              </>
            )}

            {sections.map((section) => (
              <View key={section.heading} style={styles.textSection}>
                <Text
                  role="heading"
                  aria-level={2}
                  style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
                  {section.heading}
                </Text>
                {section.paragraphs.map((paragraph, index) => (
                  <Text key={index} style={[styles.intro, { color: theme.text }]}>
                    {paragraph}
                  </Text>
                ))}
              </View>
            ))}

            {cityLinks.length > 0 ? (
              <View style={styles.textSection}>
                <Text
                  role="heading"
                  aria-level={2}
                  style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
                  {t('wineTours.cityLinksHeading')}
                </Text>
                {cityLinks.map((link) => (
                  <MoreLink
                    key={link.id}
                    label={link.label}
                    href={{ pathname: '/[lang]/wine-tours/[city]', params: { lang: locale, city: link.id } }}
                  />
                ))}
              </View>
            ) : null}

            {faq.length > 0 ? (
              <View style={styles.faqBox}>
                <Text
                  role="heading"
                  aria-level={2}
                  style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
                  {t('wineTours.faqHeading')}
                </Text>
                {faq.map((entry) => (
                  <View key={entry.q} style={styles.faqItem}>
                    <Text style={[styles.faqQuestion, { color: theme.text }]}>{entry.q}</Text>
                    <Text style={[styles.faqAnswer, { color: theme.textSecondary }]}>{entry.a}</Text>
                  </View>
                ))}
              </View>
            ) : null}

            <View style={styles.moreLinks}>
              {city && cityGuide ? (
                <MoreLink
                  label={cityGuide.title}
                  href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              <MoreLink
                label={t('wineTours.allTours')}
                href={{ pathname: '/[lang]/tours', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

function CityChip({ label, active, href }: { label: string; active: boolean; href: Href }) {
  const theme = useTheme();
  return (
    // The pill styling lives on an inner View: <Link asChild> drops a
    // Pressable's function-style `style` on web, which left these chips as
    // bare text with the icon stacked above the label.
    <Link href={href} asChild>
      <Pressable accessibilityRole="link">
        {({ pressed }) => (
          <View
            style={[
              styles.chip,
              {
                backgroundColor: active ? theme.accent : theme.card,
                borderColor: active ? theme.accent : theme.border,
              },
              pressed && { opacity: 0.85 },
            ]}>
            <MapPin size={12} color={active ? theme.accentForeground : theme.mutedForeground} />
            <Text style={[styles.chipText, { color: active ? theme.accentForeground : theme.text }]}>
              {label}
            </Text>
          </View>
        )}
      </Pressable>
    </Link>
  );
}

function MoreLink({ label, href }: { label: string; href: Href }) {
  const theme = useTheme();
  return (
    <Link href={href} asChild>
      <Pressable accessibilityRole="link" style={styles.moreLink}>
        {({ pressed }) => (
          <>
            <Text style={[styles.moreLinkText, { color: theme.primary }, pressed && { opacity: 0.6 }]}>
              {label}
            </Text>
            <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
          </>
        )}
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  stats: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: -Spacing.two,
  },
  intro: {
    fontSize: 15,
    lineHeight: 23,
  },
  note: {
    fontSize: 13,
    marginTop: -Spacing.two,
  },
  citiesBox: {
    gap: Spacing.two,
  },
  citiesHeading: {
    fontSize: 13,
    fontWeight: '600',
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one + 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  sectionHeading: {
    fontSize: 22,
    fontWeight: '700',
    marginTop: Spacing.two,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  textSection: {
    gap: Spacing.two,
  },
  faqBox: {
    gap: Spacing.three,
  },
  faqItem: {
    gap: 4,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: '700',
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 21,
  },
  moreLinks: {
    marginTop: Spacing.two,
    gap: Spacing.two,
  },
  moreLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  moreLinkText: {
    fontSize: 14,
    fontWeight: '600',
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
});
